export type validTyes = string | number | boolean | undefined;

export const CSV_UTF8_BOM = "\uFEFF";

function escapeCSV(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }

  const str = String(value).replace(/\r\n|\r|\n/g, "\n");

  // Need to escape if the value contains any of these special characters
  const needsEscape = str.includes('"') || str.includes(",") || str.includes("\n") || str.startsWith(" ") || str.endsWith(" ");

  if (!needsEscape) {
    return str;
  }

  return `"${str.replace(/"/g, '""')}"`;
}

export function arrayToCsvRow(row: validTyes[], delimiter: "," | ";" = ",") {
  return row.map(escapeCSV).join(delimiter);
}

export const arrayToCsv = (data: (validTyes | validTyes[])[][]) => {
  const rows: string[] = [];

  data.forEach((array) => {
    const expandedRows: validTyes[][] = [];
    let maxLength = 1;

    // Find the maximum array length
    array.forEach((item) => {
      if (Array.isArray(item)) {
        maxLength = Math.max(maxLength, item.length);
      }
    });

    // Create rows based on the maximum length
    for (let i = 0; i < maxLength; i++) {
      const row = array.map((item) => (Array.isArray(item) ? item[i] || "" : item));
      expandedRows.push(row);
    }

    rows.push(...expandedRows.map((row) => arrayToCsvRow(row)));
  });

  return rows.join("\r\n");
};

export const parseCSV = (str: string) => {
  const cleanStr = str.trim();
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = "";
  let insideQuotes = false;

  for (let i = 0; i < cleanStr.length; i++) {
    const char = cleanStr[i];
    const nextChar = cleanStr[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Double quotes inside quotes
        currentField += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote mode
        insideQuotes = !insideQuotes;
      }
    } else if (char === "," && !insideQuotes) {
      // End of field
      currentRow.push(currentField);
      currentField = "";
    } else if ((char === "\n" || (char === "\r" && nextChar === "\n")) && !insideQuotes) {
      // End of row
      currentRow.push(currentField);
      rows.push(currentRow);
      currentRow = [];
      currentField = "";
      if (char === "\r") i++; // Skip \n if \r\n
    } else {
      currentField += char;
    }
  }

  // Add the last field and row if there's any
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField);
    rows.push(currentRow);
  }

  const [rawHeader, ...dataRows] = rows;
  if (!rawHeader) return { header: [], data: [] };
  const header = rawHeader.map((h) => h.normalize("NFC"));
  const data: Record<string, string>[] = dataRows
    .map((row) => row.reduce((acc, cur, i) => ({ ...acc, [header[i]]: cur }), {}))
    .filter((obj) => Object.keys(obj).length > 0);

  return { header, data };
};

export const processCustomColumnNames = (fields: string[], defaultDisplayNames: Record<string, string>, customColumnNames?: string[]): string[] => {
  let displayNames = fields.map((field) => defaultDisplayNames[field]);

  if (customColumnNames && customColumnNames.length > 0) {
    const customNameMap: Record<string, string> = {};

    customColumnNames.forEach((item) => {
      const [key, value] = item.split(":");
      if (key && value) {
        customNameMap[key] = value;
      }
    });

    displayNames = fields.map((field) => customNameMap[field] || defaultDisplayNames[field]);
  }

  return displayNames;
};
